import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, User as UserIcon, BarChart3, ClipboardList, ShieldCheck, GraduationCap, Trash2, FileText, BookOpenText, CalendarRange, Cloud, CloudOff, CreditCard, Sparkles, Calculator, Compass, Menu, Wand2, Settings, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { useAuth } from "../contexts/AuthContext";
import { deleteMyAccount } from "../lib/auth";
import { submitDeletionRequest } from "../lib/api";
import { driveStatus, driveConnect, driveDisconnect } from "../lib/api";
import { openBillingPortal } from "../lib/billing";
import WhySkillsHover, { WhySkillsMobileDialog } from "./WhySkillsHover";
import EmailSignInDialog from "./EmailSignInDialog";

const initials = (name = "") => {
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const UserMenu = ({ showDashboardLink = true }) => {
  const { user, loading, signIn, signOut, openProfileModal, replayFirstRun } = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Mobile-only "Why skills?" Dialog state — lifted into UserMenu so
  // the hamburger DropdownMenuItem can close its parent dropdown and
  // open the Dialog in the same gesture without nested-portal jank.
  const [whyOpen, setWhyOpen] = useState(false);
  // Google Drive state (Phase 48). `null` = still loading; falsey
  // `configured` means the site owner hasn't given us OAuth keys yet and
  // we hide the Drive menu items entirely.
  const [drive, setDrive] = useState(null);
  const [driveBusy, setDriveBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  // Mobile sign-in-with-email dialog state. Controlled here so the
  // hamburger menu item can open the dialog after closing the dropdown
  // without nested-portal weirdness.
  const [mobileEmailOpen, setMobileEmailOpen] = useState(false);

  useEffect(() => {
    if (!user) {
      setDrive(null);
      return;
    }
    let cancelled = false;
    driveStatus()
      .then((s) => {
        if (!cancelled) setDrive(s);
      })
      .catch(() => {
        if (!cancelled) setDrive({ configured: false, connected: false });
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleDriveConnect = async () => {
    setDriveBusy(true);
    try {
      const { authorization_url } = await driveConnect();
      // Send the teacher to Google. They'll come back to /dashboard?drive_connected=1.
      window.location.href = authorization_url;
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't start Drive connection";
      toast.error(String(detail));
      setDriveBusy(false);
    }
  };

  const handleDriveDisconnect = async () => {
    if (!window.confirm("Disconnect Google Drive? Atomic Edu will stop being able to save new files to your Drive. Files already saved stay where they are.")) return;
    setDriveBusy(true);
    try {
      await driveDisconnect();
      setDrive((d) => ({ ...(d || {}), connected: false }));
      toast.success("Google Drive disconnected");
    } catch (err) {
      toast.error("Couldn't disconnect — try again");
    } finally {
      setDriveBusy(false);
    }
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      setSigningOut(false);
    }
  };

  const handleManageBilling = async () => {
    setPortalBusy(true);
    try {
      const { url } = await openBillingPortal();
      window.location.href = url;
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't open the billing portal";
      toast.error(String(detail));
      setPortalBusy(false);
    }
  };

  const handleDeleteAccount = async () => {
    // Two-step typed confirmation. We want this to be slightly annoying —
    // it is irreversible.
    const first = window.confirm(
      "Delete your Atomic Edu account?\n\n" +
        "This permanently removes:\n" +
        "• every lesson and unit in your library\n" +
        "• your classroom profile and Tracker data\n" +
        "• every active session\n\n" +
        "Your generated PDFs and Word/PowerPoint files already on " +
        "your computer are NOT affected.\n\n" +
        "We cannot recover any of this afterwards."
    );
    if (!first) return;
    const typed = window.prompt(
      'Type the word DELETE (in capitals) to confirm.'
    );
    if (typed !== "DELETE") {
      toast.message("Account deletion cancelled.");
      return;
    }
    setDeleting(true);
    try {
      // Phase 93 — notify info@ before the destructive call so the
      // team has a record of the request even if delete fails. Fire-
      // and-forget; we don't want a Resend hiccup to block deletion.
      submitDeletionRequest({ reason: "User-initiated from UserMenu" }).catch(() => {});
      const res = await deleteMyAccount();
      toast.success("Account deleted.", {
        description: `Removed ${res.activities_deleted} activities, ${res.sessions_deleted} sessions. Take care, teacher.`,
      });
      // Force-refresh so the auth context drops the now-invalid user and
      // every cached query starts from scratch.
      window.location.assign("/");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Delete failed";
      toast.error("Could not delete your account", { description: String(detail) });
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div
        data-testid="user-menu-loading"
        className="h-10 w-24 rounded-lg bg-white/10 border border-white/10 animate-pulse"
      />
    );
  }

  if (!user) {
    return (
      <div className="flex items-center gap-1">
        <WhySkillsMobileDialog open={whyOpen} onOpenChange={setWhyOpen} />
        <Link
          to="/skills"
          data-testid="nav-skills"
          className="hidden lg:inline-flex items-center gap-1 text-[13px] font-semibold text-white/80 hover:text-white px-2.5 py-1.5 rounded-md hover:bg-white/10"
        >
          <BookOpenText className="h-3.5 w-3.5" /> Skills Library
        </Link>
        <Link
          to="/why-skills"
          data-testid="nav-why-skills"
          className="hidden lg:inline-flex items-center gap-1 text-[13px] font-semibold text-white/80 hover:text-white px-2.5 py-1.5 rounded-md hover:bg-white/10"
        >
          <HelpCircle className="h-3.5 w-3.5" /> Why skills?
        </Link>
        <Link
          to="/the-path"
          data-testid="nav-the-path"
          className="hidden lg:inline-flex items-center gap-1 text-[13px] font-semibold text-white/80 hover:text-white px-2.5 py-1.5 rounded-md hover:bg-white/10"
        >
          <Compass className="h-3.5 w-3.5" /> The Path
        </Link>
        {/* Mobile-only hamburger — surfaces every nav link the desktop
            header shows so phones never need horizontal scrolling
            (Phase 105). Tapping any item navigates and the menu
            auto-closes. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild data-testid="mobile-nav-trigger">
            <button
              type="button"
              className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-md text-white/80 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-[#96ded1]/40"
              aria-label="Open navigation menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={8}
            className="bg-white border-[#4a6984]/15 w-[calc(100vw-2rem)] max-w-sm"
            data-testid="mobile-nav-content"
          >
            <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 text-base">
              <Link to="/skills" data-testid="mobile-nav-skills">
                <BookOpenText className="h-4 w-4 mr-2" /> Skills Library
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => setWhyOpen(true)}
              data-testid="mobile-nav-why-skills"
              className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 text-base"
            >
              <HelpCircle className="h-4 w-4 mr-2" /> Why skills?
            </DropdownMenuItem>
            <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 text-base">
              <Link to="/the-path" data-testid="mobile-nav-the-path">
                <Compass className="h-4 w-4 mr-2" /> The Path
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 text-base">
              <Link to="/samples" data-testid="mobile-nav-samples">
                <FileText className="h-4 w-4 mr-2" /> See Sample Lessons
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 text-base">
              <Link to="/pricing" data-testid="mobile-nav-pricing">
                <CreditCard className="h-4 w-4 mr-2" /> Plans
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {/* Sign-in entry points — collapsed into the hamburger on
                mobile so the nav stops overflowing on narrow phones
                (the inline Google / Email buttons below are
                `hidden md:inline-flex`). */}
            <DropdownMenuItem
              onSelect={signIn}
              data-testid="mobile-sign-in-google"
              className="text-[#1c2d3d] focus:bg-[#f4f5f7] font-bold py-3 text-base"
            >
              <UserIcon className="h-4 w-4 mr-2" /> Sign in with Google
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setMobileEmailOpen(true);
              }}
              data-testid="mobile-sign-in-email"
              className="text-[#1c2d3d] focus:bg-[#f4f5f7] font-bold py-3 text-base"
            >
              <UserIcon className="h-4 w-4 mr-2" /> Sign in with email
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {/* The mobile sign-in-with-email dialog is rendered via a hidden
            EmailSignInDialog whose Dialog stays controlled by
            `mobileEmailOpen` — the menu item above flips that state and
            then the existing dialog UI handles capture & send. */}
        <EmailSignInDialog
          mobileOpen={mobileEmailOpen}
          onMobileOpenChange={setMobileEmailOpen}
        />
        <span className="hidden lg:inline-flex items-center gap-1.5"><Button
          data-testid="sign-in-with-google"
          onClick={signIn}
          className="ae-cta h-9 px-3.5 rounded-lg text-[13px] font-semibold inline-flex items-center gap-1.5"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="#1c2d3d"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"
            />
            <path
              fill="#1c2d3d"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#1c2d3d"
              d="M5.84 14.12A6.96 6.96 0 0 1 5.47 12c0-.74.13-1.45.37-2.12V7.04H2.18A10.99 10.99 0 0 0 1 12c0 1.78.43 3.46 1.18 4.96l3.66-2.84z"
            />
            <path
              fill="#1c2d3d"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
            />
          </svg>
          Sign in with Google
        </Button>
        <EmailSignInDialog /></span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 sm:gap-2">
      <WhySkillsMobileDialog open={whyOpen} onOpenChange={setWhyOpen} />
      <Link
        to="/skills"
        data-testid="nav-skills"
        className="hidden lg:inline-flex items-center gap-1.5 text-sm font-semibold text-white/80 hover:text-white px-3 py-2 rounded-md hover:bg-white/10"
      >
        <BookOpenText className="h-3.5 w-3.5" /> Skills Library
      </Link>
      <Link
        to="/why-skills"
        data-testid="nav-why-skills"
        className="hidden lg:inline-flex items-center gap-1.5 text-sm font-semibold text-white/80 hover:text-white px-3 py-2 rounded-md hover:bg-white/10"
      >
        <HelpCircle className="h-3.5 w-3.5" /> Why skills?
      </Link>
      <Link
        to="/the-path"
        data-testid="nav-the-path"
        className="hidden lg:inline-flex items-center gap-1.5 text-sm font-semibold text-white/80 hover:text-white px-3 py-2 rounded-md hover:bg-white/10"
      >
        <Compass className="h-3.5 w-3.5" /> The Path
      </Link>

      {/* Mobile-only hamburger — surfaces every desktop nav link plus
          the Tools menu items AND the full account section in one tap
          (Phase 105). Stays md:hidden so the desktop layout above
          keeps its independent links. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild data-testid="mobile-nav-trigger">
          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-md text-white/80 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-[#96ded1]/40"
            aria-label="Open navigation menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={8}
          className="bg-white border-[#4a6984]/15 w-[calc(100vw-2rem)] max-w-sm max-h-[80vh] overflow-y-auto"
          data-testid="mobile-nav-content"
        >
          {/* Signed-in identity strip at the top so teachers can
              confirm which account they're in before tapping anything. */}
          <DropdownMenuLabel className="text-[#4a6984]">
            <div className="flex items-center gap-3">
              {user.picture ? (
                <img
                  src={user.picture}
                  alt=""
                  className="h-9 w-9 rounded-full object-cover shrink-0"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span className="h-9 w-9 shrink-0 rounded-full bg-[#96ded1] text-[#1c2d3d] grid place-items-center font-extrabold text-sm">
                  {initials(user.name || user.email)}
                </span>
              )}
              <div className="min-w-0">
                <div className="font-extrabold truncate" data-testid="mobile-nav-user-name">{user.name || "Teacher"}</div>
                <div className="text-xs font-medium text-[#4a6984]/70 truncate" data-testid="mobile-nav-user-email">
                  {user.email}
                </div>
              </div>
            </div>
            {user.subscription?.tier_name && (
              <div
                data-testid="mobile-nav-tier-badge"
                className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#96ded1]/35 text-[#1c2d3d] text-[10px] font-extrabold uppercase tracking-[0.12em]"
              >
                <Sparkles className="h-2.5 w-2.5" />
                {user.subscription.tier_name}
                {user.subscription.status === "trialing" && " · Trial"}
              </div>
            )}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 text-base">
            <Link to="/skills" data-testid="mobile-nav-skills">
              <BookOpenText className="h-4 w-4 mr-2" /> Skills Library
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => setWhyOpen(true)}
            data-testid="mobile-nav-why-skills"
            className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 text-base"
          >
            <HelpCircle className="h-4 w-4 mr-2" /> Why skills?
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 text-base">
            <Link to="/the-path" data-testid="mobile-nav-the-path">
              <Compass className="h-4 w-4 mr-2" /> The Path
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 text-base">
            <Link to="/samples" data-testid="mobile-nav-samples">
              <FileText className="h-4 w-4 mr-2" /> See Sample Lessons
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 text-base">
            <Link to="/pricing" data-testid="mobile-nav-pricing">
              <CreditCard className="h-4 w-4 mr-2" /> Plans
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#4a6984]/70">
            Tools
          </DropdownMenuLabel>
          <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 text-base">
            <Link to="/generator" data-testid="mobile-nav-generator">
              <Sparkles className="h-4 w-4 mr-2" /> The Generator
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 text-base">
            <Link to="/generator#skills-generator" data-testid="mobile-nav-skills-generator">
              <Wand2 className="h-4 w-4 mr-2" /> Skills Generator
            </Link>
          </DropdownMenuItem>
          {(user.is_admin || user.has_math_engine_access) && (
            <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 text-base">
              <Link to="/math-engine" data-testid="mobile-nav-math-engine">
                <Calculator className="h-4 w-4 mr-2" /> Math Engine
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 text-base">
            <Link to="/long-range-plan" data-testid="mobile-nav-long-range-plan">
              <CalendarRange className="h-4 w-4 mr-2" /> Long Range Plan
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 text-base">
            <Link to="/gradebook" data-testid="mobile-nav-gradebook">
              <ClipboardList className="h-4 w-4 mr-2" /> Gradebook
            </Link>
          </DropdownMenuItem>
          {showDashboardLink && (
            <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 text-base">
              <Link to="/dashboard" data-testid="mobile-nav-dashboard">
                <BarChart3 className="h-4 w-4 mr-2" /> Teacher Dashboard
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#4a6984]/70">
            Account
          </DropdownMenuLabel>
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              openProfileModal();
            }}
            data-testid="mobile-nav-classroom-profile"
            className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 text-base cursor-pointer"
          >
            <GraduationCap className="h-4 w-4 mr-2" /> Classroom profile
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              replayFirstRun?.();
            }}
            data-testid="mobile-nav-show-me-how"
            className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 text-base cursor-pointer"
          >
            <HelpCircle className="h-4 w-4 mr-2" /> Show me how this works
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 text-base">
            <Link to="/account" data-testid="mobile-nav-account-settings">
              <Settings className="h-4 w-4 mr-2" /> Account settings
            </Link>
          </DropdownMenuItem>
          {user.is_admin && (
            <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 text-base">
              <Link to="/admin" data-testid="mobile-nav-admin">
                <ShieldCheck className="h-4 w-4 mr-2" /> Admin panel
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 text-base">
            <Link to="/privacy" data-testid="mobile-nav-privacy" target="_blank" rel="noreferrer">
              <FileText className="h-4 w-4 mr-2" /> Privacy & Terms
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={handleSignOut}
            data-testid="mobile-nav-sign-out"
            disabled={signingOut}
            className="text-[#4a6984] focus:bg-[#f4f5f7] py-3 text-base cursor-pointer font-semibold"
          >
            <LogOut className="h-4 w-4 mr-2" />
            {signingOut ? "Signing out…" : "Sign out"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Tools menu — generator / skills generator / math engine / dashboard */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild data-testid="tools-menu-trigger">
          <button
            type="button"
            className="hidden lg:inline-flex items-center gap-1.5 text-sm font-semibold text-white/80 hover:text-white px-3 py-2 rounded-md hover:bg-white/10"
            aria-label="Tools menu"
          >
            <Menu className="h-4 w-4" /> Menu
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="bg-white border-[#4a6984]/15 w-56"
        >
          <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7]">
            <Link to="/generator" data-testid="tools-menu-generator">
              <Sparkles className="h-3.5 w-3.5 mr-2" /> The Generator
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7]">
            <Link to="/generator#skills-generator" data-testid="tools-menu-skills-generator">
              <Wand2 className="h-3.5 w-3.5 mr-2" /> Skills Generator
            </Link>
          </DropdownMenuItem>
          {(user.is_admin || user.has_math_engine_access) && (
            <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7]">
              <Link to="/math-engine" data-testid="tools-menu-math-engine">
                <Calculator className="h-3.5 w-3.5 mr-2" /> Math Engine
              </Link>
            </DropdownMenuItem>
          )}
          {showDashboardLink && (
            <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7]">
              <Link to="/dashboard" data-testid="tools-menu-dashboard">
                <BarChart3 className="h-3.5 w-3.5 mr-2" /> Teacher Dashboard
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7]">
            <Link to="/long-range-plan" data-testid="tools-menu-long-range-plan">
              <CalendarRange className="h-3.5 w-3.5 mr-2" /> Long Range Plan
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7]">
            <Link to="/gradebook" data-testid="tools-menu-gradebook">
              <ClipboardList className="h-3.5 w-3.5 mr-2" /> Gradebook
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger
          asChild
          data-testid="user-menu-trigger"
        >
          <button
            className="hidden md:flex items-center gap-2 rounded-full p-1 pr-3 border border-white/20 bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="User menu"
          >
            {user.picture ? (
              <img
                src={user.picture}
                alt=""
                className="h-8 w-8 rounded-full object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              <span className="h-8 w-8 rounded-full bg-[#96ded1] text-[#1c2d3d] grid place-items-center font-extrabold text-sm">
                {initials(user.name || user.email)}
              </span>
            )}
            <span className="text-sm font-semibold text-white hidden sm:inline">
              {user.name?.split(" ")[0] || user.email}
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="bg-white border-[#4a6984]/15 w-60"
        >
          <DropdownMenuLabel className="text-[#4a6984]">
            <div className="font-extrabold truncate">{user.name || "Teacher"}</div>
            <div className="text-xs font-medium text-[#4a6984]/70 truncate">
              {user.email}
            </div>
            {user.subscription?.tier_name && (
              <div
                data-testid="user-menu-tier-badge"
                className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#96ded1]/35 text-[#1c2d3d] text-[10px] font-extrabold uppercase tracking-[0.12em]"
              >
                <Sparkles className="h-2.5 w-2.5" />
                {user.subscription.tier_name}
                {user.subscription.status === "trialing" && " · Trial"}
              </div>
            )}
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-[#4a6984]/10" />
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              openProfileModal();
            }}
            data-testid="user-menu-classroom-profile"
            className="text-[#4a6984] focus:bg-[#f4f5f7] cursor-pointer"
          >
            <GraduationCap className="h-3.5 w-3.5 mr-2" /> Classroom profile
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              replayFirstRun?.();
            }}
            data-testid="user-menu-show-me-how"
            className="text-[#4a6984] focus:bg-[#f4f5f7] cursor-pointer"
          >
            <HelpCircle className="h-3.5 w-3.5 mr-2" /> Show me how this works
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7]">
            <Link to="/account" data-testid="user-menu-account-settings">
              <Settings className="h-3.5 w-3.5 mr-2" /> Account settings
            </Link>
          </DropdownMenuItem>
          {drive?.configured && (
            drive.connected ? (
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  handleDriveDisconnect();
                }}
                data-testid="user-menu-drive-disconnect"
                disabled={driveBusy}
                className="text-[#4a6984] focus:bg-[#f4f5f7] cursor-pointer"
              >
                <CloudOff className="h-3.5 w-3.5 mr-2" />
                {driveBusy ? "Working…" : "Disconnect Google Drive"}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  handleDriveConnect();
                }}
                data-testid="user-menu-drive-connect"
                disabled={driveBusy}
                className="text-[#4a6984] focus:bg-[#f4f5f7] cursor-pointer"
              >
                <Cloud className="h-3.5 w-3.5 mr-2" />
                {driveBusy ? "Opening Google…" : "Connect Google Drive"}
              </DropdownMenuItem>
            )
          )}
          <DropdownMenuSeparator className="bg-[#4a6984]/10" />
          {/* Subscription / billing items hidden during beta — re-enable
              when Stripe goes live. Logic kept below for easy reactivation. */}
          {false && (user.subscription?.status ? (
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                handleManageBilling();
              }}
              data-testid="user-menu-manage-billing"
              disabled={portalBusy}
              className="text-[#4a6984] focus:bg-[#f4f5f7] cursor-pointer"
            >
              <CreditCard className="h-3.5 w-3.5 mr-2" />
              {portalBusy ? "Opening Stripe…" : "Manage subscription"}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7]">
              <Link to="/pricing" data-testid="user-menu-pricing">
                <CreditCard className="h-3.5 w-3.5 mr-2" /> Plans & pricing
              </Link>
            </DropdownMenuItem>
          ))}
          {user.is_admin && (
            <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7]">
              <Link to="/admin" data-testid="user-menu-admin">
                <ShieldCheck className="h-3.5 w-3.5 mr-2" /> Admin panel
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem asChild className="text-[#4a6984] focus:bg-[#f4f5f7]">
            <Link to="/privacy" data-testid="user-menu-privacy" target="_blank" rel="noreferrer">
              <FileText className="h-3.5 w-3.5 mr-2" /> Privacy & Terms
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              handleDeleteAccount();
            }}
            data-testid="user-menu-delete-account"
            disabled={deleting}
            className="text-[#b94a4a] focus:bg-[#fff0f0] focus:text-[#b94a4a] cursor-pointer"
          >
            <Trash2 className="h-3.5 w-3.5 mr-2" />
            {deleting ? "Deleting…" : "Delete account"}
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-[#4a6984]/10" />
          <DropdownMenuItem
            onSelect={handleSignOut}
            data-testid="sign-out-button"
            disabled={signingOut}
            className="text-[#4a6984] focus:bg-[#f4f5f7] cursor-pointer"
          >
            <LogOut className="h-3.5 w-3.5 mr-2" />
            {signingOut ? "Signing out…" : "Sign out"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default UserMenu;
